import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Loader, ListChecks } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import { CountingListLineRequest, CreateCountingListRequest } from '../../../../types/countingListCreation';

/**
 * One selected (warehouse, item, bin) line from the comparison view.
 * Bins are already filtered to those that are countable and not on active count list
 * (UI does this via disabled checkboxes), but we re-validate in the modal anyway.
 */
export interface SelectedLocationLine {
  warehouseID: string;
  itemNo: string;
  description?: string;
  binCode: string;
  /** Current WMS quantity at this bin (used as expectedQuantity for variance calc). */
  wmsQuantity: number;
}

interface OperatorOption {
  operatorID: string;
  operatorName?: string;
}

interface CreateCountFromDiffModalProps {
  isOpen: boolean;
  selectedLines: SelectedLocationLine[];
  warehouseID: string;
  operators: OperatorOption[];
  onClose: () => void;
  onSuccess: (batchID: string, linesCreated: number) => void;
}

/**
 * Modal: opprett telleliste fra valgte avvik-linjer.
 * Reuses CountingListCreationService.createCountingList in the backend
 * (LineTypeOrigin = 4, no new type — see Q&A decision).
 */
export const CreateCountFromDiffModal: React.FC<CreateCountFromDiffModalProps> = ({
  isOpen,
  selectedLines,
  warehouseID,
  operators,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const [operatorId, setOperatorId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset modal state on open
  useEffect(() => {
    if (isOpen) {
      const today = new Date().toLocaleDateString('no-NO');
      setDescription(`ERP-avvik ${today}`);
      setOperatorId('');
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !submitting) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  // Sammendrag
  const uniqueItems = new Set(selectedLines.map((l) => l.itemNo));
  const totalLines = selectedLines.length;

  const canSubmit = totalLines > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const lines: CountingListLineRequest[] = selectedLines.map((l) => ({
        itemNo: l.itemNo,
        binCode: l.binCode,
        warehouseID: l.warehouseID,
        expectedQuantity: l.wmsQuantity,
      }));

      const request: CreateCountingListRequest = {
        description,
        operatorID: operatorId || undefined,
        warehouse: warehouseID,
        lines,
      };

      const response = await countingListCreationService.createCountingList(request);

      if (response.success) {
        onSuccess(response.batchID, response.linesCreated);
      } else {
        // Backend returns rich error message when conflicts found
        setError(response.errorMessage || t('common.status.error'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.status.error');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ListChecks size={20} />
            {t('warehouse.modules.balanceComparison.createCount.title')}
          </h3>
          {!submitting && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              aria-label={t('common.buttons.close')}
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Selection summary */}
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
            <div>
              <strong>{totalLines}</strong>{' '}
              {t('warehouse.modules.balanceComparison.createCount.lineCount')}
            </div>
            <div>
              <strong>{uniqueItems.size}</strong>{' '}
              {t('warehouse.modules.balanceComparison.createCount.itemCount')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t('warehouse.modules.balanceComparison.createCount.warehouse')}: <strong>{warehouseID}</strong>
            </div>
          </div>

          {/* Operator dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('warehouse.modules.balanceComparison.createCount.operator')}
            </label>
            <select
              value={operatorId}
              onChange={(e) => setOperatorId(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            >
              <option value="">
                {t('warehouse.modules.balanceComparison.createCount.operatorUnassigned')}
              </option>
              {operators.map((op) => (
                <option key={op.operatorID} value={op.operatorID}>
                  {op.operatorName ? `${op.operatorName} (${op.operatorID})` : op.operatorID}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('warehouse.modules.balanceComparison.createCount.descriptionLabel')}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              maxLength={200}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 text-sm border border-red-200 dark:border-red-800">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <Loader size={16} className="animate-spin" />}
            {t('warehouse.modules.balanceComparison.createCount.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
